'use client';

import Text, { TEXT_VARIANT } from '@pixum/text';
import { useRouter } from 'next/navigation';
import { HTTP_STATUS_CODE, getLocaleParts } from 'utils';
import { usePayPalButtons } from '../../../../hooks/paypal-express';
import { signupAsCustomerForPayPalExpressAction } from '../../../../actions/paypal-express/signupAsCustomerForPayPalExpressAction';
import { PAYPAL_ERROR_CODES, REFRESH_SITE_STORAGE_KEY } from '../../../../constants';
import type { PayPalSmartButtonProps } from '../types';
import styles from './PayPalSmartButton.module.css';

const PP_PARAM = '?ppExpress=1';

const PAYPAL_BUTTON_STYLE = {
  layout: 'horizontal',
  disableMaxWidth: true,
  height: 42,
  shape: 'pill',
  tagline: false,
} as const;

export function PayPalSmartButton({
  routes,
  tagline,
  locale,
  onGeneralError,
  onShippingAddressError,
}: PayPalSmartButtonProps) {
  const router = useRouter();
  const { languageCode: language, countryCode: country } = getLocaleParts(locale);

  async function handleRegisterUser() {
    // signupAsCustomerForPayPalExpressAction is a Server Action — the HTTP call and JWT
    // management happen server-side. This eliminates @pixum/std-lib from the client bundle
    // and avoids the circular JWT dependency risk (ClientFetch → getValidJwtToken → refresh).
    const response = await signupAsCustomerForPayPalExpressAction();

    if (response.idToken) {
      // JWT cookies already set server-side by signupAsCustomerForPayPalExpressAction.
      // Notify other tabs of the auth change (mirrors the login flow in CartAuthStepClient).
      localStorage.setItem(REFRESH_SITE_STORAGE_KEY, Date.now().toString());

      router.push(`${routes.checkout}${PP_PARAM}`);
    } else if (
      response.status === HTTP_STATUS_CODE.CONFLICT ||
      response.status === HTTP_STATUS_CODE.BAD_REQUEST ||
      response.status === HTTP_STATUS_CODE.UNAUTHORIZED
    ) {
      // 409/400 — account already exists; 401 — session expired/invalid: redirect to login
      router.push(`${routes.login}${PP_PARAM}`);
    } else {
      onGeneralError();
    }
  }

  async function handleApproveResponse(response: Response) {
    if (!response.ok && response.status === HTTP_STATUS_CODE.UNPROCESSABLE_CONTENT) {
      const result = await response.json();

      if (result?.code === PAYPAL_ERROR_CODES.INVALID_ADDRESS) {
        onShippingAddressError();
      } else {
        onGeneralError();
      }

      return;
    }

    await handleRegisterUser();
  }

  const paypalRef = usePayPalButtons({
    style: PAYPAL_BUTTON_STYLE,
    language,
    country,
    onApproveResponse: handleApproveResponse,
    onGeneralError,
  });

  return (
    <div className={styles['paypal--wrapper']}>
      <div className={styles['paypal--button']}>
        <div ref={paypalRef} />
      </div>
      {tagline && (
        <div className={styles['paypal--tagline']}>
          <Text variant={TEXT_VARIANT.CAPTION}>{tagline}</Text>
        </div>
      )}
    </div>
  );
}
